"use client";
import { Button } from "@/components/ui/button";
import { CountrySelect } from "@/components/ui/country-select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useGetLoggedInUser } from "@/hooks/use-get-logged-in-user";
import { useScopedI18n } from "@/locales/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const userDataPart1 = z
    .object({
        firstName: z.string(),
        lastName: z.string(),
        organization: z.string(),
    })
    .superRefine((data, ctx) => {
        if ((data.firstName.length < 2 || data.lastName.length < 2) && data.organization.length < 2) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Either Name or Organization must be filled out",
                path: ["firstName"],
            });
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Either Name or Organization must be filled out",
                path: ["lastName"],
            });
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Either Name or Organization must be filled out",
                path: ["organization"],
            });
        }
    });

const userDataPart2 = z.object({
    addressLine1: z.string().min(2),
    addressLine2: z.string(),
    city: z.string().min(2),
    stateProvinceRegion: z.string(),
    postalCode: z.string().min(2),
    countryCode: z.string().min(1),
});

const userData = userDataPart1.and(userDataPart2);

type UserData = z.infer<typeof userData>;

export function PersonalData({
    onSubmit,
    footer,
    body,
    splitInTwo = false,
}: {
    onSubmit: (values: UserData) => void;
    footer?: (children: React.ReactNode, readyToSave: boolean, readyToGoBack: boolean) => React.ReactNode;
    body?: (children: React.ReactNode) => React.ReactNode;
    splitInTwo?: boolean;
}) {
    const { data: user } = useGetLoggedInUser();

    if (user == null) return;

    const userData = {
        firstName: user.addressFirstName ?? "",
        lastName: user.addressLastName ?? "",
        organization: user.addressOrganization ?? "",
        addressLine1: user.addressLine1 ?? "",
        addressLine2: user.addressLine2 ?? "",
        city: user.addressCity ?? "",
        stateProvinceRegion: user.addressStateProvinceRegion ?? "",
        postalCode: user.addressPostalCode ?? "",
        countryCode: user.countryCode ?? "",
    };

    if (splitInTwo) {
        return <PersonalDataSplit userData={userData} onSubmit={onSubmit} footer={footer} body={body} />;
    }

    return <PersonalDataSingle userData={userData} onSubmit={onSubmit} footer={footer} body={body} />;
}

function PersonalDataSplit({
    userData,
    onSubmit,
    footer,
    body,
}: {
    userData: UserData | null;
    onSubmit: (values: UserData) => void;
    footer?: (children: React.ReactNode, readyToSave: boolean, readyToGoBack: boolean) => React.ReactNode;
    body?: (children: React.ReactNode) => React.ReactNode;
}) {
    const [screen, setScreen] = useState(0);

    const [part1Screen, setPart1Screen] = useState<z.infer<typeof userDataPart1> | null>(null);

    return (
        <div>
            {screen === 0 && (
                <PersonalDataSplitPart1
                    userData1={userData}
                    onSubmit={(values) => {
                        setScreen(1);
                        setPart1Screen(values);
                    }}
                    body={body}
                    footer={footer}
                />
            )}
            {screen === 1 && (
                <PersonalDataSplitPart2
                    userData2={userData}
                    onSubmit={(values) => {
                        if (part1Screen == null) {
                            throw new Error("Part 1 screen is null");
                        }
                        onSubmit({ ...part1Screen, ...values });
                    }}
                    goBack={() => setScreen(0)}
                    body={body}
                    footer={footer}
                />
            )}
        </div>
    );
}

function PersonalDataSplitPart1({
    userData1,
    onSubmit,
    body,
    footer,
}: {
    userData1: z.infer<typeof userDataPart1> | null;
    onSubmit: (values: z.infer<typeof userDataPart1>) => void;
    body?: (children: React.ReactNode) => React.ReactNode;
    footer?: (children: React.ReactNode, readyToSave: boolean, readyToGoBack: boolean) => React.ReactNode;
}) {
    const t = useScopedI18n("personalData");
    const form = useForm<z.infer<typeof userDataPart1>>({
        resolver: zodResolver(userDataPart1),
        defaultValues: userData1 ?? undefined,
    });

    const bodyInternal = body ?? ((children) => <>{children}</>);
    const footerInternal = footer ?? ((children) => <>{children}</>);

    return (
        <Form {...form}>
            <form
                onSubmit={(v) => {
                    v.preventDefault();
                    void form.handleSubmit(onSubmit)(v);
                }}
                className="w-full"
            >
                {bodyInternal(
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-2">
                            <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("form.firstName.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("form.firstName.placeholder")} {...field} />
                                        </FormControl>
                                        <FormDescription>{t("form.firstName.description")}</FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("form.lastName.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("form.lastName.placeholder")} {...field} />
                                        </FormControl>
                                        <FormDescription>{t("form.lastName.description")}</FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <hr />
                        <FormField
                            control={form.control}
                            name="organization"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("form.organization.label")}</FormLabel>
                                    <FormControl>
                                        <Input placeholder={t("form.organization.placeholder")} {...field} />
                                    </FormControl>
                                    <FormDescription>{t("form.organization.description")}</FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>,
                )}
                {footerInternal(
                    <Button key="continue" type="submit">
                        {t("buttons.continue")}
                    </Button>,
                    false,
                    true,
                )}
            </form>
        </Form>
    );
}

function PersonalDataSplitPart2({
    userData2,
    onSubmit,
    goBack,
    body,
    footer,
}: {
    userData2: z.infer<typeof userDataPart2> | null;
    onSubmit: (values: z.infer<typeof userDataPart2>) => void;
    goBack: () => void;
    body?: (children: React.ReactNode) => React.ReactNode;
    footer?: (children: React.ReactNode, readyToSave: boolean, readyToGoBack: boolean) => React.ReactNode;
}) {
    const t = useScopedI18n("personalData");
    const form = useForm<z.infer<typeof userDataPart2>>({
        resolver: zodResolver(userDataPart2),
        defaultValues: userData2 ?? undefined,
    });

    const bodyInternal = body ?? ((children) => <>{children}</>);
    const footerInternal = footer ?? ((children) => <>{children}</>);

    return (
        <Form {...form}>
            <form
                onSubmit={(v) => {
                    v.preventDefault();
                    void form.handleSubmit(onSubmit)(v);
                }}
                className="w-full"
            >
                {bodyInternal(
                    <div className="space-y-4">
                        <FormField
                            control={form.control}
                            name="addressLine1"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("form.addressLine1.label")}</FormLabel>
                                    <FormControl>
                                        <Input placeholder={t("form.addressLine1.placeholder")} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="addressLine2"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("form.addressLine2.label")}</FormLabel>
                                    <FormControl>
                                        <Input placeholder={t("form.addressLine2.placeholder")} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="grid grid-cols-2 gap-2">
                            <FormField
                                control={form.control}
                                name="city"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("form.city.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("form.city.placeholder")} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="stateProvinceRegion"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("form.state.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("form.state.placeholder")} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <FormField
                                control={form.control}
                                name="postalCode"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("form.postalCode.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("form.postalCode.placeholder")} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="countryCode"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="">{t("form.country.label")}</FormLabel>
                                        <CountrySelect {...field} />
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>,
                )}
                {footerInternal(
                    <Button key="back-internal" type="button" variant="outline" onClick={goBack}>
                        {t("buttons.goBack")}
                    </Button>,
                    true,
                    false,
                )}
            </form>
        </Form>
    );
}

function PersonalDataSingle({
    userData: defaultData,
    onSubmit,
    footer,
    body,
}: {
    userData: UserData | null;
    onSubmit: (values: UserData) => void;
    footer?: (children: React.ReactNode, readyToSave: boolean, readyToGoBack: boolean) => React.ReactNode;
    body?: (children: React.ReactNode) => React.ReactNode;
}) {
    const t = useScopedI18n("personalData");
    const form = useForm<UserData>({
        resolver: zodResolver(userData),
        defaultValues: defaultData ?? undefined,
    });

    const bodyInternal = body ?? ((children) => <>{children}</>);
    const footerInternal = footer ?? ((children) => <>{children}</>);

    return (
        <Form {...form}>
            <form
                onSubmit={(v) => {
                    v.preventDefault();
                    void form.handleSubmit(onSubmit)(v);
                }}
                className="w-full"
            >
                {bodyInternal(
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 items-start gap-3">
                            <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("form.firstName.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("form.firstName.placeholder")} {...field} />
                                        </FormControl>
                                        <FormDescription>{t("form.firstName.description")}</FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("form.lastName.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("form.lastName.placeholder")} {...field} />
                                        </FormControl>
                                        <FormDescription>{t("form.lastName.description")}</FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <hr />
                        <FormField
                            control={form.control}
                            name="organization"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("form.organization.label")}</FormLabel>
                                    <FormControl>
                                        <Input placeholder={t("form.organization.placeholder")} {...field} />
                                    </FormControl>
                                    <FormDescription>{t("form.organization.description")}</FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="addressLine1"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("form.addressLine1.label")}</FormLabel>
                                    <FormControl>
                                        <Input placeholder={t("form.addressLine1.placeholder")} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="addressLine2"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("form.addressLine2.label")}</FormLabel>
                                    <FormControl>
                                        <Input placeholder={t("form.addressLine2.placeholder")} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="grid grid-cols-2 gap-3">
                            <FormField
                                control={form.control}
                                name="city"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("form.city.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("form.city.placeholder")} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="stateProvinceRegion"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("form.state.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("form.state.placeholder")} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <FormField
                                control={form.control}
                                name="postalCode"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("form.postalCode.label")}</FormLabel>
                                        <FormControl>
                                            <Input placeholder={t("form.postalCode.placeholder")} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="countryCode"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="">{t("form.country.label")}</FormLabel>
                                        <CountrySelect {...field} />
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>,
                )}
                {footerInternal(<div key="internal-footer" />, true, true)}
            </form>
        </Form>
    );
}
